import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Alert } from '@mui/material';
import {
    AppContext,
    SttDialog,
    SttFormTextarea,
    SttSelectUnlockerType,
} from '../all';

type Unlocker = {
    id?: number;
    type: string;
    code?: string;
};

type Props = {
    idUnlocker?: number;
    idUser?: number;
    idSC?: number;
    open?: boolean;
    onClose?: () => void;
    onDelete?: (unlocker: Unlocker) => void;
    onSave?: (unlocker: Unlocker) => void;
};

export function SttUnlockerDialog({
    idUnlocker,
    idUser,
    idSC,
    open = false,
    onClose,
    onDelete,
    onSave,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [unlocker, setUnlocker] = useState<Unlocker | undefined>();

    /**
     * Init
     */
    useEffect(() => {
        if (idUnlocker) loadUnlocker();
        else {
            setUnlocker({
                type: cxt?.constants?.unlockerTypes?.[0]?.id || '',
            });
        }
    }, [idUnlocker]);

    /**
     * Load unlocker
     */
    const loadUnlocker = () => {
        cxt?.api('GET', `/unlockers/${idUnlocker}`, {
            params: { trash: true },
            success: (r: { unlocker: Unlocker }) => {
                setUnlocker(r.unlocker);
            },
        });
    };

    /**
     * Save
     */
    const save = () => {
        if (!unlocker) return;

        // Update
        if (unlocker.id) {
            // Update unlocker
            cxt?.api('PUT', `/unlockers/${idUnlocker}`, {
                params: {
                    type: unlocker.type,
                    code: unlocker.code,
                },
                success: (r: { unlocker: Unlocker }) => {
                    setUnlocker(r.unlocker);
                    cxt?.showMessage('S', t('messages.saved'));
                },
            });
        } else {
            // Create unlocker
            cxt?.api('POST', `/scs/${idSC}/users/${idUser}/unlockers`, {
                params: {
                    type: unlocker.type,
                    code: unlocker.code,
                },
                success: (r: { unlocker: Unlocker }) => {
                    cxt?.showMessage('S', t('messages.saved'));
                    setUnlocker(undefined); // clear state
                    onClose?.();
                    if (onSave) onSave(r.unlocker);
                },
            });
        }
    };

    /**
     * Remove
     */
    const remove = () => {
        cxt?.api('DELETE', `/unlockers/${idUnlocker}`, {
            confirmation: true,
            success: (r: { unlocker: Unlocker }) => {
                cxt?.showMessage('S', t('messages.deleted'));
                onClose?.();
                if (onDelete) onDelete(r.unlocker);
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setUnlocker({ ...unlocker, [name]: value } as Unlocker);
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('user.unlocker')}
            open={open}
            onClose={onClose}
            content={
                <>
                    {unlocker && (
                        <Grid container spacing={3}>
                            <Grid size={{ md: 6 }}>
                                <SttSelectUnlockerType
                                    name="type"
                                    defVal={unlocker.type}
                                    onChange={handleForm}
                                />
                            </Grid>

                            <SttFormTextarea
                                grid
                                name="code"
                                caption={t('labels.code')}
                                defVal={unlocker.code}
                                onChange={handleForm}
                            />
                        </Grid>
                    )}

                    {unlocker && unlocker.type === 'PIN' && !unlocker.id && (
                        <Box mt={2}>
                            <Alert severity="info">
                                {t('unlocker.generatePIN')}
                            </Alert>
                        </Box>
                    )}

                    {unlocker && unlocker.type === 'QR' && unlocker.id && (
                        <Box sx={{ textAlign: 'center' }} mx="auto">
                            <img
                                src={cxt?.getResource(
                                    `/unlockers/${unlocker.id}.svg`,
                                )}
                                width="250px"
                            />
                        </Box>
                    )}
                </>
            }
            buttons={[
                {
                    type: 'delete',
                    onClick: () => remove(),
                    show: unlocker?.id !== undefined,
                },
                {
                    type: 'save',
                    onClick: () => save(),
                },
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
